import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { SosAuthProvider } from '@/contexts/AuthContext';
import { authorizationService } from '@/services/authService';
import { extractIdentifiers, filterAndMapObjects } from '@/utils/menuUtils.js';
import { EventType } from '@azure/msal-browser';
import { useMsal } from '@azure/msal-react';
import { msalInstance } from '@/constants/authConfig';
import { navigationMenus } from '@/constants/navigationMenus.js';
import { AccessDenied } from '@/layout/common-pages/index.js';
import { setUserProfile, clearUserProfile } from '@/slices/userSlice.js';
import { extractInitials } from '@/utils/userUtils.js';

// If no active account is set, default to the first available account
if (
  !msalInstance.getActiveAccount() &&
  msalInstance.getAllAccounts().length > 0
) {
  msalInstance.setActiveAccount(msalInstance.getAllAccounts()[0]);
}

/**
 * Clears all browser caches using the Cache API.
 * This is typically used during authentication reset to remove any potentially stale cached data.
 */
function clearCaches() {
  if ('caches' in window) {
    caches.keys().then((cacheNames) => {
      cacheNames.forEach((cacheName) => {
        caches.delete(cacheName);
      });
    });
  }
}

// Event listener to set active account after a successful login
msalInstance.addEventCallback((event) => {
  if (event.eventType === EventType.LOGIN_SUCCESS && event.payload?.account) {
    msalInstance.setActiveAccount(event.payload.account);
  }
});

// Higher-Order Component that wraps a given component with Azure MSAL authentication and authorization logic.
const withMsalAuth = (WrappedComponent) => {
  const WithMsalAuth = (props) => {
    const [menu, setMenu] = useState([]);
    const [featureList, setFeatureList] = useState([]);
    const [haveAccess, setHaveAccess] = useState(true);
    const [redirectHandled, setRedirectHandled] = useState(false);

    const { instance, accounts } = useMsal();
    const dispatch = useDispatch();

    // Step 1: Ensure the MSAL redirect flow is completed before proceeding
    useEffect(() => {
      const processRedirect = async () => {
        try {
          await instance.initialize(); // Ensure MSAL is initialized
          const response = await instance.handleRedirectPromise();
          if (response?.account) {
            instance.setActiveAccount(response.account);
          }
        } catch (error) {
          console.error('MSAL redirect handling error:', error);
        } finally {
          setRedirectHandled(true); // Mark redirect process as handled
        }
      };

      processRedirect();
    }, [instance]);

    // Step 2: Handle authentication and authorization after redirect is completed
    useEffect(() => {
      if (!redirectHandled) return;

      const handleAuthFlow = async () => {
        try {
          // If no accounts are available, clear storage and redirect to login
          if (accounts.length === 0) {
            localStorage.clear();
            clearCaches();
            dispatch(clearUserProfile());
            await instance.loginRedirect();
            return; // Will return to this flow after login
          }

          // Set user profile in Redux from MSAL account
          const userAccount = accounts[0];
          if (userAccount) {
            const userName = userAccount.name || 'NA';
            const userInitials = extractInitials(userName);
            dispatch(
              setUserProfile({ initials: userInitials, name: userName }),
            );
          }

          // Retrieve tokens and features from localStorage
          const storedAuth = localStorage.getItem('Authentication');
          const storedAuthorization = localStorage.getItem('Authorization');
          const storedRefresh = localStorage.getItem('Refresh');

          // If tokens are missing, fetch fresh authorization details
          if (!storedAuth || !storedAuthorization || !storedRefresh) {
            const email = accounts[0].username;
            const localPart = email.split('@')[0];
            const isNumericSsoId = /^\d+$/.test(localPart);

            const authPayload = isNumericSsoId
              ? { ssoId: parseInt(localPart, 10), email }
              : { email };

            const authorization = await authorizationService(authPayload);

            localStorage.setItem(
              'Authentication',
              authorization.authenticationToken,
            );
            localStorage.setItem(
              'Authorization',
              authorization.authorizationToken,
            );
            localStorage.setItem('Refresh', authorization.refreshToken);
            localStorage.setItem(
              'FeatureList',
              JSON.stringify(authorization.featureDetails),
            );

            setFeatureList(authorization.featureDetails);

            const identifiers = extractIdentifiers(
              authorization.featureDetails,
            );
            const menuItems = filterAndMapObjects(
              navigationMenus.navigation,
              identifiers,
            );
            localStorage.setItem('menuItems', JSON.stringify(menuItems));
            setMenu(menuItems);
          } else {
            // Use cached values from localStorage
            const cachedFeatures = JSON.parse(
              localStorage.getItem('FeatureList'),
            );
            const identifiers = extractIdentifiers(cachedFeatures);
            const menuItems = filterAndMapObjects(
              navigationMenus.navigation,
              identifiers,
            );

            setFeatureList(cachedFeatures);
            setMenu(menuItems);
          }
        } catch (error) {
          console.error('Auth error:', error);
          if (error?.status === 401 || error?.status === 403) {
            setHaveAccess(false); // Show fallback if user lacks access
          }
        }
      };

      handleAuthFlow();
    }, [redirectHandled, accounts, instance, dispatch]);

    // Prevent rendering anything until MSAL redirect is processed and user is authenticated
    if (!redirectHandled || accounts.length === 0) {
      return null;
    }

    // Wrap the target component with the authorization context
    return (
      <SosAuthProvider menus={menu} featureList={featureList}>
        {haveAccess ? <WrappedComponent {...props} /> : <AccessDenied />}
      </SosAuthProvider>
    );
  };

  return WithMsalAuth;
};

export default withMsalAuth;
